<script setup>
// [TODO] This componet is too big and bulky to be in the same file, we can consider splitting this into multiple
// composables and components, useVirtualChatList, useChatlistFilters
import {
  ref,
  unref,
  provide,
  computed,
  watch,
  onMounted,
  onUnmounted,
  defineEmits,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

// [VITE] [TODO] We are using vue-virtual-scroll for now, since that seemed the simplest way to migrate
// from the current one. But we should consider using tanstack virtual in the future
// https://tanstack.com/virtual/latest/docs/framework/vue/examples/variable
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import ChatListHeader from './ChatListHeader.vue';
// NOTICE: KAPTION-INTEGRATION - KaptionChatListHeader replaces ChatListHeader when flag enabled
import KaptionChatListHeader from './kaption/KaptionChatListHeader.vue';
// NOTICE: KAPTION-INTEGRATION - Single-pane full-text search for conversations.
import KaptionConversationSearchPane from './kaption/KaptionConversationSearchPane.vue';
// NOTICE: KAPTION-INTEGRATION - KaptionActionPill at bottom of chat list
import KaptionActionPill from './kaption/KaptionActionPill.vue';
// NOTICE: KAPTION-INTEGRATION - Sales Funnel Kanban view
import KaptionSalesKanban from './kaption/KaptionSalesKanban.vue';
import KaptionSalesKanbanTogglePill from './kaption/KaptionSalesKanbanTogglePill.vue';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer (flag + permission, fails open)
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
// NOTICE: KAPTION-INTEGRATION - Sidebar filter permission gates shared with the sidebar
import { applyAssigneeTabGates } from 'dashboard/components-next/sidebar/kaptionSidebarFilterGates';
import { useKaptionPrivacyMode } from 'dashboard/composables/useKaptionPrivacyMode';
import { useKaptionAccountScheduledMessages } from 'dashboard/composables/useKaptionAccountScheduledMessages';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import ChatTypeTabs from './widgets/ChatTypeTabs.vue';
import ConversationItem from './ConversationItem.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import IntersectionObserver from './IntersectionObserver.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import { useFilter } from 'shared/composables/useFilter';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useEventListener } from '@vueuse/core';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useKaptionFavorites } from 'dashboard/composables/useKaptionFavorites';
import subDays from 'date-fns/subDays';
import addDays from 'date-fns/addDays';
import parseISO from 'date-fns/parseISO';
import formatISO from 'date-fns/formatISO';

import { emitter } from 'shared/helpers/mitt';
// NOTICE: KAPTION-INTEGRATION - Theme event emitter for dark mode detection
import themeEventEmitter from 'shared/helpers/themeEventEmitter';

import wootConstants from 'dashboard/constants/globals';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { conversationListPageURL } from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';

import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  // NOTICE: KAPTION-INTEGRATION - Concrete agent filter from URL query.
  assigneeId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  // NOTICE: KAPTION-INTEGRATION
  assistantId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
  // NOTICE: KAPTION-INTEGRATION - Route-driven kanban view
  kanbanView: { default: false, type: Boolean },
});

// NOTICE: KAPTION-INTEGRATION - Added toggleLayout emit for KaptionChatListHeader
// kanbanActive: emits kanban view state to parent so it can hide ConversationBox
const emit = defineEmits(['conversationLoad', 'toggleLayout', 'kanbanActive']);
const { uiSettings, updateUISettings } = useUISettings();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();
const { isAdmin } = useAdmin();
const { setPersonalFavorite, setAccountFavorite, mergedFavorites } =
  useKaptionFavorites();

// NOTICE: KAPTION-INTEGRATION - Feature flag for KaptionChatListHeader
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer (flag + permission, fails open)
const { canUseFeature } = useFeaturePermissions();
// feature-permissions.NAVIGATION.2 — platform_chat_list permission + KAPTION_PLATFORM_CHAT_LIST flag
const useKaptionHeader = computed(
  () =>
    isKaptionFlagEnabled('KAPTION_PLATFORM_CHAT_LIST') &&
    canUseFeature('platform_chat_list')
);
// NOTICE: KAPTION-INTEGRATION - Account setting to default to All Conversations and hide My Inbox
// defaultAllConversations is initialized after currentAccountId is declared below

// NOTICE: KAPTION-INTEGRATION - Sales Kanban view toggle (route-driven)
// feature-permissions.NAVIGATION.5 — sales_kanban permission + KAPTION_SALES_KANBAN flag
const showKanbanToggle = computed(
  () =>
    isKaptionFlagEnabled('KAPTION_SALES_KANBAN') &&
    canUseFeature('sales_kanban')
);
// Kanban is active when driven by route prop
const isKanbanViewActive = computed(() => props.kanbanView);

// NOTICE: KAPTION-INTEGRATION - WhatsApp Web-style search inside the conversation pane.
const kaptionConversationSearchQuery = ref('');
// feature-permissions.NAVIGATION.1 — conversation_search permission + KAPTION_CONVERSATION_SEARCH flag
const isKaptionConversationSearchEnabled = computed(
  () =>
    useKaptionHeader.value &&
    isKaptionFlagEnabled('KAPTION_CONVERSATION_SEARCH') &&
    canUseFeature('conversation_search') &&
    !(showKanbanToggle.value && isKanbanViewActive.value)
);
const isKaptionConversationSearchActive = computed(
  () =>
    isKaptionConversationSearchEnabled.value &&
    kaptionConversationSearchQuery.value.trim().length > 0
);

function handleKaptionConversationSearchQueryChange(query) {
  kaptionConversationSearchQuery.value = query;
}

function toggleKanbanView() {
  const accountId = route.params.accountId;
  if (isKanbanViewActive.value) {
    // Navigate back to dashboard (list view)
    router.push({ path: `/app/accounts/${accountId}/dashboard` });
  } else {
    // Navigate to kanban route
    router.push({ path: `/app/accounts/${accountId}/kanban` });
  }
}

// Emit kanban state to parent so ConversationView can hide message pane
watch(
  () => showKanbanToggle.value && isKanbanViewActive.value,
  val => emit('kanbanActive', !!val),
  { immediate: true }
);

// NOTICE: KAPTION-INTEGRATION - Privacy mode for KaptionActionPill
const { privacyEnabled } = useKaptionPrivacyMode();
provide('kaptionPrivacyEnabled', privacyEnabled);

// NOTICE: KAPTION-INTEGRATION - Account-level scheduled messages for KaptionActionPill
const {
  scheduledMessages: actionPillScheduledMessages,
  scheduledMessagesCount: actionPillScheduledCount,
  isFetching: actionPillIsFetching,
  isCancelling: actionPillIsCancelling,
  cancelScheduledMessage: actionPillCancelScheduled,
  refresh: actionPillRefresh,
  setSearchQuery: actionPillSetSearchQuery,
  searchQuery: actionPillSearchQuery,
} = useKaptionAccountScheduledMessages();

// NOTICE: KAPTION-INTEGRATION - Dark mode detection (uses body.classList and theme event)
// Chatwoot uses document.body.classList.contains('dark') for dark mode
const isDarkModeInternal = ref(document.body.classList.contains('dark'));
const isDarkMode = computed(() => isDarkModeInternal.value);
// Theme change listener (stored for cleanup)
let themeUnsubscribe = null;

// NOTICE: KAPTION-INTEGRATION - Selection state for KaptionChatListHeader
const kaptionSelectedFavorite = ref(null);
const kaptionSelectedFolder = ref(null);
const kaptionSelectedLabel = ref(null);
const kaptionSelectedChannel = ref(null);
const kaptionSelectedTeam = ref(null);
const kaptionSelectedAssistant = ref(null);

const conversationListRef = ref(null);
const conversationDynamicScroller = ref(null);

provide('contextMenuElementTarget', conversationDynamicScroller);

// NOTICE: KAPTION-INTEGRATION - Route names that represent a conversation detail view.
// When navigating to these routes, we should NOT reset the active assignee filter.
const CONVERSATION_DETAIL_ROUTES = new Set([
  'inbox_conversation',
  'conversation_through_inbox',
  'conversations_through_label',
  'conversations_through_team',
  'conversations_through_agent',
  'conversations_through_agent_team',
  'conversations_through_folders',
  'conversation_through_mentions',
  'conversation_through_unattended',
  'conversation_through_kaption_assistants',
  'conversation_through_participating',
]);

// NOTICE: KAPTION-INTEGRATION - Session storage keys for persisting filters across navigation
const ASSIGNEE_TAB_SESSION_KEY = 'kaptionActiveAssigneeTab';
const SORT_ORDER_SESSION_KEY = 'kaptionActiveSortBy';

function isConversationDetailRoute(routeName) {
  return CONVERSATION_DETAIL_ROUTES.has(routeName);
}

// NOTICE: KAPTION-INTEGRATION - Restore persisted assignee tab from sessionStorage (if any)
function getPersistedAssigneeTab() {
  try {
    const stored = sessionStorage.getItem(ASSIGNEE_TAB_SESSION_KEY);
    if (stored && Object.values(wootConstants.ASSIGNEE_TYPE).includes(stored)) {
      return stored;
    }
  } catch {
    // sessionStorage not available, ignore
  }
  return null;
}

function persistAssigneeTab(value) {
  try {
    sessionStorage.setItem(ASSIGNEE_TAB_SESSION_KEY, value);
  } catch {
    // sessionStorage not available, ignore
  }
}

// NOTICE: KAPTION-INTEGRATION - Persist and restore sort order across navigation
function getPersistedSortOrder() {
  try {
    const stored = sessionStorage.getItem(SORT_ORDER_SESSION_KEY);
    if (stored && Object.values(wootConstants.SORT_BY_TYPE).includes(stored)) {
      return stored;
    }
  } catch {
    // sessionStorage not available, ignore
  }
  return null;
}

function persistSortOrder(value) {
  try {
    sessionStorage.setItem(SORT_ORDER_SESSION_KEY, value);
  } catch {
    // sessionStorage not available, ignore
  }
}

const activeAssigneeTab = ref(
  getPersistedAssigneeTab() || wootConstants.ASSIGNEE_TYPE.ME
);
const activeStatus = ref(wootConstants.STATUS_TYPE.OPEN);
const activeSortBy = ref(
  getPersistedSortOrder() || wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC
);
// NOTICE: KAPTION-INTEGRATION - Guard for sessionStorage persistence.
// Prevents watchers from writing the ref's initial fallback (ME) before
// onMounted has a chance to set the correct default (ALL).
const isFilterInitialized = ref(false);
const showAdvancedFilters = ref(false);
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
const foldersQuery = ref({});
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const isContextMenuOpen = ref(false);
const appliedFilter = ref([]);
// NOTICE: KAPTION-INTEGRATION - Track when URL query param filters are applied (from reports chart click)
const hasUrlFiltersApplied = ref(false);
// NOTICE: KAPTION-INTEGRATION - Track when URL date filters are active (persists after initial load)
const hasUrlDateFilters = ref(false);
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);

const currentUser = useMapGetter('getCurrentUser');
const chatLists = useMapGetter('getFilteredConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
// NOTICE: KAPTION-INTEGRATION - Unread tab list
const unreadChatsList = useMapGetter('getUnreadChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
// NOTICE: KAPTION-INTEGRATION - Groups only filter
const groupsOnlyFilter = useMapGetter('getGroupsOnlyFilter');
const effectiveGroupsOnlyFilter = computed(
  () =>
    groupsOnlyFilter.value &&
    isKaptionFlagEnabled('KAPTION_GROUPS') &&
    canUseFeature('group_conversations')
);
const activeInbox = useMapGetter('getSelectedInbox');
const conversationStats = useMapGetter('conversationStats/getStats');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');
const campaigns = useMapGetter('campaigns/getAllCampaigns');
// NOTICE: KAPTION-INTEGRATION - Use sidebar labels getter for consistency with sidebar display
const labels = useMapGetter('labels/getLabelsOnSidebar');
const currentAccountId = useMapGetter('getCurrentAccountId');
// NOTICE: KAPTION-INTEGRATION - Account setting to default to All Conversations and hide My Inbox
const getAccountForSetting = useMapGetter('accounts/getAccount');
const defaultAllConversations = computed(() => {
  const account = getAccountForSetting.value(currentAccountId.value);
  return !!account?.settings?.default_all_conversations;
});
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');
// NOTICE: KAPTION-INTEGRATION — team-filter-scope.CLIENT.1
// The client-side page filter has to accept the same rows the server did, and under
// team_and_members that includes the conversations owned by the group's people.
const getTeamMembersFn = useMapGetter('teamMembers/getTeamMembers');
// NOTICE: KAPTION-INTEGRATION - Get assistants for resolved favorites
const getAssistant = useMapGetter('kaptionAssistants/getAssistants');

// NOTICE: KAPTION-INTEGRATION - Map labels to format expected by topbar (title -> name)
const kaptionLabels = computed(() =>
  labels.value.map(label => ({
    ...label,
    name: label.title, // Topbar expects 'name', labels store uses 'title'
  }))
);

// NOTICE: KAPTION-INTEGRATION - Resolve favorites to actual objects with navigation info
const kaptionResolvedFavorites = computed(() => {
  return mergedFavorites.value
    .map(fav => {
      // Resolve favorite to actual object based on type
      if (fav.type === 'folder') {
        const folder = folders.value.find(f => f.id === fav.id);
        return folder ? { ...fav, name: folder.name, resolved: folder } : null;
      }
      if (fav.type === 'label') {
        const label = labels.value.find(l => l.id === fav.id);
        return label ? { ...fav, name: label.title, resolved: label } : null;
      }
      if (fav.type === 'inbox') {
        const inbox = inboxesList.value.find(i => i.id === fav.id);
        return inbox ? { ...fav, name: inbox.name, resolved: inbox } : null;
      }
      if (fav.type === 'team') {
        const team = teamsList.value.find(item => item.id === fav.id);
        return team ? { ...fav, name: team.name, resolved: team } : null;
      }
      if (fav.type === 'assistant') {
        const assistant = getAssistant.value.find(a => a.id === fav.id);
        return assistant
          ? { ...fav, name: assistant.name, resolved: assistant }
          : null;
      }
      return null;
    })
    .filter(Boolean);
});

useChatListKeyboardEvents(conversationListRef);
const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent,
  onAssignLabels,
  onAssignTeamsForBulk,
  onUpdateConversations,
  onAssignAssistant,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

// computed
const intersectionObserverOptions = computed(() => {
  return {
    root: conversationListRef.value,
    rootMargin: '100px 0px 100px 0px',
  };
});

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  // NOTICE: KAPTION-INTEGRATION - Include URL date filters
  return (
    hasAppliedFilters.value || hasActiveFolders.value || hasUrlDateFilters.value
  );
});

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const assigneeTabItems = computed(() => {
  // NOTICE: KAPTION-INTEGRATION - Custom tab order for kaption assistants
  let tabs = filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  );

  // For kaption_assistants view, show only 'all' and 'unassigned' tabs in that order
  if (props.conversationType === 'kaption_assistants') {
    tabs = tabs.filter(item => item.key === 'all' || item.key === 'unassigned');
    tabs.sort((a, b) => {
      if (a.key === 'all') return -1;
      if (b.key === 'all') return 1;
      return 0;
    });
  }

  // NOTICE: KAPTION-INTEGRATION - Hide "me" tab when defaultAllConversations flag is enabled
  if (defaultAllConversations.value) {
    tabs = tabs.filter(item => item.key !== 'me');
  }

  // NOTICE: KAPTION-INTEGRATION - Sidebar filter permission scopes gate the
  // matching tabs too, so a filter hidden in the sidebar disappears everywhere.
  tabs = applyAssigneeTabGates(tabs, canUseFeature);

  return tabs.map(tab => ({
    key: tab.key,
    name: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${tab.key}`),
    // NOTICE: KAPTION-INTEGRATION - explanatory tooltip (filter definitions)
    tooltip: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS_TOOLTIP.${tab.key}`),
    // NOTICE: KAPTION-INTEGRATION - "unread" tab is computed client-side
    // because conversationStats doesn't (yet) expose an unread count from the API.
    // Use only the status filter — label/team/inbox scoping isn't relevant for a
    // top-level Unread badge, and avoids a forward reference to conversationFilters.
    count:
      tab.key === wootConstants.ASSIGNEE_TYPE.UNREAD
        ? unreadChatsList.value({ status: activeStatus.value }).length
        : conversationStats.value?.[tab.count] || 0,
  }));
});

const showAssigneeInConversationCard = computed(() => {
  return (
    hasAppliedFiltersOrActiveFolders.value ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL ||
    // NOTICE: KAPTION-INTEGRATION - unread-filter.TAB.5: the Unread view spans
    // every assignee (like All), so the card assignee stays visible there too.
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNREAD
  );
});

const currentPageFilterKey = computed(() => {
  return hasAppliedFiltersOrActiveFolders.value
    ? 'appliedFilters'
    : activeAssigneeTab.value;
});

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  activeAssigneeTab
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

const activeAssigneeTabCount = computed(() => {
  const tabItem = assigneeTabItems.value.find(
    item => item.key === activeAssigneeTab.value
  );
  return tabItem?.count || 0;
});

const conversationListPagination = computed(() => {
  const conversationsPerPage = 25;
  const hasChatsOnView =
    chatsOnView.value &&
    Array.isArray(chatsOnView.value) &&
    !chatsOnView.value.length;
  const isNoFiltersOrFoldersAndChatListNotEmpty =
    !hasAppliedFiltersOrActiveFolders.value && hasChatsOnView;
  const isUnderPerPage =
    chatsOnView.value.length < conversationsPerPage &&
    activeAssigneeTabCount.value < conversationsPerPage &&
    activeAssigneeTabCount.value > chatsOnView.value.length;

  if (isNoFiltersOrFoldersAndChatListNotEmpty && isUnderPerPage) {
    return 1;
  }

  return currentPage.value + 1;
});

const conversationFilters = computed(() => {
  // NOTICE: KAPTION-INTEGRATION - Groups filter takes precedence over route-based conversationType
  let conversationType = props.conversationType || undefined;
  if (effectiveGroupsOnlyFilter.value) {
    conversationType = 'groups';
  }
  // NOTICE: KAPTION-INTEGRATION - Unread tab: pass conversation_type=unread to backend
  // and reset assigneeType to 'all' so the server-side filter doesn't double-scope.
  let assigneeType = activeAssigneeTab.value;
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNREAD) {
    conversationType = 'unread';
    assigneeType = wootConstants.ASSIGNEE_TYPE.ALL;
  }
  // NOTICE: KAPTION-INTEGRATION - Full-path concrete filters always span all
  // assignee tabs; assignee_id/team_id provide the actual server scope.
  if (props.assigneeId || props.teamId) {
    assigneeType = wootConstants.ASSIGNEE_TYPE.ALL;
  }

  return {
    inboxId: props.conversationInbox ? props.conversationInbox : undefined,
    assigneeType,
    assigneeId: props.assigneeId || undefined,
    status: activeStatus.value,
    sortBy: activeSortBy.value,
    page: conversationListPagination.value,
    labels: props.label ? [props.label] : undefined,
    teamId: props.teamId || undefined,
    conversationType,
    // NOTICE: KAPTION-INTEGRATION - Pass assistant ID for filtering conversations by assistant
    assistantId: props.assistantId || undefined,
  };
});

// NOTICE: KAPTION-INTEGRATION — team-filter-scope.CLIENT.1
const activeTeamMemberIds = computed(() => {
  if (!props.teamId) return [];
  return (getTeamMembersFn.value(props.teamId) || []).map(member => member.id);
});

const activeTeam = computed(() => {
  if (props.teamId) {
    return (
      getTeamFn.value(props.teamId) || {
        id: Number(props.teamId),
        name: `#${props.teamId}`,
      }
    );
  }
  return {};
});

// NOTICE: KAPTION-INTEGRATION - Resolve full-path filters for the visible
// summary, with stable ID fallbacks while the stores are loading.
const activeAgent = computed(() => {
  if (!props.assigneeId) return null;
  return (
    agentList.value.find(agent => agent.id === Number(props.assigneeId)) || {
      id: Number(props.assigneeId),
      name: `#${props.assigneeId}`,
    }
  );
});

const activeRouteFilters = computed(() => {
  const filters = [];
  if (props.assigneeId) {
    filters.push({
      key: 'agent',
      icon: 'i-lucide-user-round',
      label: `${t('KAPTION_PLATFORM.CHAT_LIST.AGENT')}:`,
      value: activeAgent.value?.name,
    });
  }
  if (props.teamId) {
    filters.push({
      key: 'team',
      icon: 'i-lucide-users-round',
      label: `${t('KAPTION_PLATFORM.CHAT_LIST.TEAM')}:`,
      value: activeTeam.value?.name || `#${props.teamId}`,
    });
  }
  return filters;
});

const hasActiveRouteFilters = computed(
  () => activeRouteFilters.value.length > 0
);

const pageTitle = computed(() => {
  if (hasAppliedFilters.value) {
    return t('CHAT_LIST.TAB_HEADING');
  }
  if (inbox.value.name) {
    return inbox.value.name;
  }
  if (activeTeam.value.name) {
    return activeAgent.value?.name
      ? `${activeAgent.value.name} · ${activeTeam.value.name}`
      : activeTeam.value.name;
  }
  if (activeAgent.value?.name) {
    return activeAgent.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (props.conversationType === 'mention') {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (props.conversationType === 'participating') {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === 'unattended') {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  // NOTICE: KAPTION-INTEGRATION
  if (props.conversationType === 'kaption_assistants') {
    if (props.assistantId) {
      const assistant = getAssistant.value.find(
        a => a.id === Number(props.assistantId)
      );
      return assistant
        ? assistant.name
        : t('SIDEBAR.KAPTION_ASSISTANTS_CONVERSATIONS');
    }
    return t('SIDEBAR.KAPTION_ASSISTANTS_CONVERSATIONS');
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

const conversationList = computed(() => {
  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    // NOTICE: KAPTION-INTEGRATION — team-filter-scope.CLIENT.1
    // teamMemberIds rides with the CLIENT-side filters only; conversationFilters is
    // what goes to the server and must keep its shape.
    const filters = {
      ...conversationFilters.value,
      teamMemberIds: activeTeamMemberIds.value,
    };
    if (activeAssigneeTab.value === 'me') {
      localConversationList = [...mineChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'unassigned') {
      localConversationList = [...unAssignedChatsList.value(filters)];
    } else if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNREAD) {
      // NOTICE: KAPTION-INTEGRATION - Unread tab: filter to conversations with unread_count > 0
      localConversationList = [...unreadChatsList.value(filters)];
    } else {
      localConversationList = [...allChatList.value(filters)];
    }
  } else {
    localConversationList = [...chatLists.value];
  }

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }

  return localConversationList;
});

// NOTICE: KAPTION-INTEGRATION - Decorate the visible list with two pseudo-items
// (`__pinned_section_header__` and `__other_section_header__`) when at least one
// pinned conversation is present. Real conversation rows are unchanged so all
// downstream logic (selection, pagination, chatsOnView mirror) keeps working.
// SID: conversation-pinning.LIST_ORDER.2 conversation-pinning.LIST_ORDER.3 conversation-pinning.LIST_ORDER.5
const decoratedConversationList = computed(() => {
  const list = conversationList.value;
  if (!list || list.length === 0) return [];

  const firstUnpinnedIndex = list.findIndex(c => !c.pinned);
  const hasPinned = firstUnpinnedIndex !== 0 && list.some(c => c.pinned);
  if (!hasPinned) return list;

  const pinned = list.filter(c => c.pinned);
  const others = list.filter(c => !c.pinned);

  const headers = [
    {
      __separator: true,
      id: '__pinned_section_header__',
      label: t('CHAT_LIST.PINNED_SECTION'),
    },
  ];
  if (others.length > 0) {
    headers.push({
      __separator: true,
      id: '__other_section_header__',
      label: t('CHAT_LIST.ALL_OTHER_SECTION'),
    });
  }

  return [
    headers[0],
    ...pinned,
    ...(headers[1] ? [headers[1]] : []),
    ...others,
  ];
});

const showEndOfListMessage = computed(() => {
  return (
    conversationList.value.length &&
    hasCurrentPageEndReached.value &&
    !chatListLoading.value
  );
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  const { status, order_by: orderBy } = filterBy;
  activeStatus.value = status || wootConstants.STATUS_TYPE.OPEN;
  activeSortBy.value = Object.values(wootConstants.SORT_BY_TYPE).includes(
    orderBy
  )
    ? orderBy
    : wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
}

function emitConversationLoaded() {
  emit('conversationLoad');
  // [VITE] removing this since the library has changed
  // nextTick(() => {
  //   // Addressing a known issue in the virtual list library where dynamically added items
  //   // might not render correctly. This workaround involves a slight manual adjustment
  //   // to the scroll position, triggering the list to refresh its rendering.
  //   const virtualList = conversationListRef.value;
  //   const scrollToOffset = virtualList?.scrollToOffset;
  //   const currentOffset = virtualList?.getOffset() || 0;
  //   if (scrollToOffset) {
  //     scrollToOffset(currentOffset + 1);
  //   }
  // });
}

function fetchFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
    })
    .then(emitConversationLoaded);

  showAdvancedFilters.value = false;
}

function fetchSavedFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
    })
    .then(emitConversationLoaded);
}

function onApplyFilter(payload) {
  payload = useSnakeCase(payload);
  resetBulkActions();
  foldersQuery.value = filterQueryGenerator(payload);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchFilteredConversations(payload);
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  appliedFilter.value = [];
}

async function onUpdateSavedFilter(payload, folderName, favorites = {}) {
  const transformedPayload = useSnakeCase(payload);
  const folderId = unref(activeFolder)?.id;
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };

  await store.dispatch('customViews/update', payloadData);

  if (typeof favorites?.favoriteForMe === 'boolean' && folderId) {
    setPersonalFavorite('folder', folderId, favorites.favoriteForMe);
  }

  if (
    isAdmin.value &&
    typeof favorites?.favoriteForAccount === 'boolean' &&
    folderId
  ) {
    await setAccountFavorite('folder', folderId, favorites.favoriteForAccount);
  }
  closeAdvanceFiltersModal();
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    campaigns: campaigns.value,
    languages: languages,
    countries: countries,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    props.conversationInbox,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

// NOTICE: KAPTION-INTEGRATION
// team-filter-scope.CLIENT.2 — never issue the same request twice in a row.
//
// conversationListPagination pins the page at 1 whenever the rendered list is empty
// while the server's count says it should not be. Any disagreement between the server
// scope and the client-side page filter therefore becomes an unbounded refetch of
// page 1 rather than an empty list — measured at ~4 requests/second. CLIENT.1 removes
// the disagreement we know about; this removes the amplifier, so the next one costs a
// wrong list instead of a hammered API. resetAndFetchData clears the signature, so an
// explicit reset always refetches.
const lastFetchSignature = ref(null);

function fetchConversations() {
  const signature = JSON.stringify(conversationFilters.value);
  if (signature === lastFetchSignature.value) return;

  lastFetchSignature.value = signature;
  store.dispatch('updateChatListFilters', conversationFilters.value);
  store.dispatch('fetchAllConversations').then(emitConversationLoaded);
}

function resetAndFetchData() {
  // NOTICE: KAPTION-INTEGRATION - Skip reset if URL filters are applied (from reports chart click)
  // This prevents watchers from clearing the date range filters on initial load
  if (hasUrlFiltersApplied.value) {
    return;
  }

  appliedFilter.value = [];
  resetBulkActions();
  // NOTICE: KAPTION-INTEGRATION — team-filter-scope.CLIENT.2
  lastFetchSignature.value = null;
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  fetchConversations();
}

function loadMoreConversations() {
  if (hasCurrentPageEndReached.value || chatListLoading.value) {
    return;
  }

  if (!hasAppliedFiltersOrActiveFolders.value) {
    fetchConversations();
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  }
}

// Add a method to handle scroll events
function handleScroll() {
  const scroller = conversationDynamicScroller.value;
  if (scroller && scroller.hasScrollbar) {
    const { scrollTop, scrollHeight, clientHeight } = scroller.$el;
    if (scrollHeight - (scrollTop + clientHeight) < 100) {
      loadMoreConversations();
    }
  }
}

function updateAssigneeTab(selectedTab) {
  if (activeAssigneeTab.value !== selectedTab) {
    resetBulkActions();
    emitter.emit('clearSearchInput');
    activeAssigneeTab.value = selectedTab;
    if (!currentPage.value) {
      fetchConversations();
    }
  }
}

function onBasicFilterChange(value, type) {
  if (type === 'status') {
    activeStatus.value = value;
  } else if (type === 'sort') {
    activeSortBy.value = value;
  }
  // NOTICE: KAPTION-INTEGRATION - conversationType is handled via store getter (groupsOnlyFilter)
  // so we just need to trigger a refetch when it changes
  resetAndFetchData();
}

// NOTICE: KAPTION-INTEGRATION - Event handlers for KaptionChatListHeader
function clearKaptionSelections() {
  kaptionSelectedFavorite.value = null;
  kaptionSelectedFolder.value = null;
  kaptionSelectedLabel.value = null;
  kaptionSelectedChannel.value = null;
  kaptionSelectedTeam.value = null;
  kaptionSelectedAssistant.value = null;
}

function handleKaptionFilterChange(filter) {
  // Map 'mine' to 'me' for compatibility with existing tab system.
  // NOTICE: KAPTION-INTEGRATION - 'unread' is a Kaption-specific filter that
  // surfaces conversations with unread incoming messages for the current agent.
  const mapping = {
    all: 'all',
    mine: 'me',
    unassigned: 'unassigned',
    unread: 'unread',
  };
  const mappedTab = mapping[filter] || 'all';
  // Clear any Kaption selections when filter changes
  clearKaptionSelections();
  updateAssigneeTab(mappedTab);
}

function handleKaptionStatusChange(status) {
  // Clear any Kaption selections when status filter changes
  clearKaptionSelections();
  activeStatus.value = status;
  resetAndFetchData();
}

function handleKaptionSortChange(sortValue) {
  // Update sort and persist to UISettings (same as onBasicFilterChange for sort)
  activeSortBy.value = sortValue;
  store.dispatch('setChatSortFilter', sortValue);
  updateUISettings({
    conversations_filter_by: {
      status: activeStatus.value,
      order_by: sortValue,
    },
  });

  // NOTICE: KAPTION-INTEGRATION - Sync sort to URL for persistence/sharing
  const newQuery = { ...route.query };
  // Only add to URL if not the default sort
  if (sortValue !== wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC) {
    newQuery.sort = sortValue;
  } else {
    delete newQuery.sort;
  }
  router.replace({ query: newQuery });

  resetAndFetchData();
}

function handleKaptionFavoriteClick(favorite) {
  kaptionSelectedFavorite.value = favorite;
  kaptionSelectedFolder.value = null;
  kaptionSelectedLabel.value = null;
  kaptionSelectedChannel.value = null;
  kaptionSelectedTeam.value = null;
  kaptionSelectedAssistant.value = null;

  // Navigate based on favorite type using resolved object
  if (!favorite?.type || !favorite?.id) return;

  if (favorite.type === 'folder') {
    router.push({
      name: 'folder_conversations',
      params: { id: favorite.id },
    });
  } else if (favorite.type === 'label' && favorite.resolved?.title) {
    router.push({
      name: 'label_conversations',
      params: { label: favorite.resolved.title },
    });
  } else if (favorite.type === 'inbox') {
    router.push({
      name: 'inbox_dashboard',
      params: { inbox_id: favorite.id },
    });
  } else if (favorite.type === 'team') {
    router.push({
      name: 'team_conversations',
      params: { teamId: favorite.id },
    });
  } else if (favorite.type === 'assistant') {
    router.push({
      name: 'conversation_kaption_assistant',
      params: { assistantId: favorite.id },
    });
  }
}

function handleKaptionFolderClick(folder) {
  kaptionSelectedFavorite.value = null;
  kaptionSelectedFolder.value = folder;
  kaptionSelectedLabel.value = null;
  kaptionSelectedChannel.value = null;
  kaptionSelectedTeam.value = null;
  kaptionSelectedAssistant.value = null;
  // Navigate to folder view
  if (folder?.id) {
    router.push({
      name: 'folder_conversations',
      params: { id: folder.id },
    });
  }
}

function handleKaptionLabelClick(label) {
  kaptionSelectedFavorite.value = null;
  kaptionSelectedFolder.value = null;
  kaptionSelectedLabel.value = label;
  kaptionSelectedChannel.value = null;
  kaptionSelectedTeam.value = null;
  kaptionSelectedAssistant.value = null;
  // Navigate to label view - use name (mapped from title) or title
  const labelName = label?.name || label?.title;
  if (labelName) {
    router.push({
      name: 'label_conversations',
      params: { label: labelName },
    });
  }
}

function handleKaptionChannelClick(channel) {
  kaptionSelectedFavorite.value = null;
  kaptionSelectedFolder.value = null;
  kaptionSelectedLabel.value = null;
  kaptionSelectedChannel.value = channel;
  kaptionSelectedTeam.value = null;
  kaptionSelectedAssistant.value = null;
  // Navigate to inbox view
  if (channel?.id) {
    router.push({
      name: 'inbox_dashboard',
      params: { inbox_id: channel.id },
    });
  }
}

function handleKaptionTeamClick(team) {
  kaptionSelectedFavorite.value = null;
  kaptionSelectedFolder.value = null;
  kaptionSelectedLabel.value = null;
  kaptionSelectedChannel.value = null;
  kaptionSelectedTeam.value = team;
  kaptionSelectedAssistant.value = null;
  // Navigate to team conversations view
  if (team?.id) {
    router.push({
      name: 'team_conversations',
      params: { teamId: team.id },
    });
  }
}

function handleKaptionAssistantClick(assistant) {
  kaptionSelectedFavorite.value = null;
  kaptionSelectedFolder.value = null;
  kaptionSelectedLabel.value = null;
  kaptionSelectedChannel.value = null;
  kaptionSelectedTeam.value = null;
  kaptionSelectedAssistant.value = assistant;
  // Navigate to assistant conversations view
  if (assistant?.id) {
    router.push({
      name: 'conversation_kaption_assistant',
      params: { assistantId: assistant.id },
    });
  }
}

// NOTICE: KAPTION-INTEGRATION - Handle account-level scheduled message cancel
function handleAccountScheduledCancel({ messageId, conversationId }) {
  actionPillCancelScheduled(conversationId, messageId);
}

// Handle toggling favorites from the topbar dropdown panels
function handleKaptionToggleFavorite({ type, item, isFavorited }) {
  // Toggle the favorite using the Kaption favorites composable
  // isFavorited indicates if the item is currently a favorite (so we should remove it)
  setPersonalFavorite(type, item.id, !isFavorited);
}

// NOTICE: KAPTION-INTEGRATION - Computed: detect "All Conversations" view
// This is true when on the home route with no category filters active
const isAllConversationsView = computed(() => {
  const routeName = route.name;
  // Must be on the home route (dashboard)
  if (routeName !== 'home' && routeName !== 'inbox_conversation') return false;
  // Must not have any category selected
  if (props.teamId) return false;
  if (props.assigneeId) return false;
  if (props.conversationInbox) return false;
  if (props.label) return false;
  if (props.foldersId) return false;
  if (props.assistantId) return false;
  // Must not be on mentions/unattended view
  if (props.conversationType === 'mention') return false;
  if (props.conversationType === 'unattended') return false;
  return true;
});

// NOTICE: KAPTION-INTEGRATION - Navigation handlers for extended filter cycle
function handleKaptionNavigateToMentions() {
  // Clear selections and navigate to mentions view
  clearKaptionSelections();
  router.push({
    name: 'conversation_mentions',
  });
}

function handleKaptionNavigateToUnattended() {
  // Clear selections and navigate to unattended view
  clearKaptionSelections();
  router.push({
    name: 'conversation_unattended',
  });
}

function handleKaptionNavigateToAllConversations(filter) {
  // Clear selections and navigate to all conversations with the specified filter
  clearKaptionSelections();
  // Map filter to assignee tab
  // NOTICE: KAPTION-INTEGRATION - 'unread' filter sets activeAssigneeTab='unread'
  // which causes ConversationFinder to receive conversation_type=unread server-side.
  const mapping = {
    all: 'all',
    mine: 'me',
    unassigned: 'unassigned',
    unread: 'unread',
  };
  const mappedTab = mapping[filter] || 'all';
  activeAssigneeTab.value = mappedTab;

  // Build query params for URL - this keeps sidebar highlighting in sync
  const query = {};
  if (filter === 'mine') {
    query.assignee = 'me';
  } else if (filter === 'unassigned') {
    query.assignee = 'unassigned';
  } else if (filter === 'unread') {
    query.assignee = 'unread';
  }
  // For 'all', we don't add query params (clean URL)

  router.push({
    name: 'home',
    query,
  });
}

function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

function redirectToConversationList() {
  const {
    params: { accountId, inbox_id: inboxId, label, agentId, teamId },
    name,
  } = route;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = 'mention';
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = 'unattended';
  }
  router.push(
    conversationListPageURL({
      accountId,
      conversationType: conversationType,
      customViewId: props.foldersId,
      inboxId,
      label,
      assigneeId: agentId,
      teamId,
    })
  );
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store.dispatch('assignPriority', { conversationId, priority }).then(() => {
    useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
      newValue: priority,
      from: 'Context menu',
    });
    useAlert(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority,
        conversationId,
      })
    );
  });
}

async function markAsUnread(conversationId) {
  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    redirectToConversationList();
  } catch (error) {
    // Ignore error
  }
}
async function markAsRead(conversationId) {
  try {
    await store.dispatch('markMessagesRead', {
      id: conversationId,
    });
  } catch (error) {
    // Ignore error
  }
}

// NOTICE: KAPTION-INTEGRATION - Bulk "Mark all as read" for the Unread filter view.
// SID: mark-all-read.UI.1
const markAllReadDialogRef = ref(null);
const isMarkingAllRead = ref(false);

// Show on any standard view (All / Mine / Unassigned / Unread) plus
// inbox/label/team/assistant routes — the service scopes the UPDATE to the
// same params the list is rendered with (inbox_id, status, assignee_type,
// team_id, labels, assistant_id, conversation_type), and the confirmation
// dialog warns "every conversation in the current view" before it runs.
//
// Saved folders & advanced filters are still excluded because the service
// doesn't accept arbitrary filter payloads — relaxing that requires routing
// the UPDATE through Conversations::FilterService.
const canMarkAllRead = computed(
  () =>
    !hasAppliedFiltersOrActiveFolders.value &&
    conversationList.value.length > 0 &&
    !(showKanbanToggle.value && isKanbanViewActive.value)
);

function openMarkAllReadDialog() {
  markAllReadDialogRef.value?.open();
}

async function confirmMarkAllRead() {
  if (isMarkingAllRead.value) return;
  isMarkingAllRead.value = true;
  try {
    const updatedCount = await store.dispatch(
      'markAllRead',
      useSnakeCase(conversationFilters.value)
    );
    markAllReadDialogRef.value?.close();
    useAlert(
      updatedCount > 0
        ? t('CHAT_LIST.MARK_ALL_READ.SUCCESS', { count: updatedCount })
        : t('CHAT_LIST.MARK_ALL_READ.SUCCESS_NONE')
    );
    // Refresh the list so the just-read conversations drop out of the Unread view.
    resetAndFetchData();
  } catch (error) {
    useAlert(t('CHAT_LIST.MARK_ALL_READ.ERROR'));
  } finally {
    isMarkingAllRead.value = false;
  }
}

async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

function toggleConversationStatus(conversationId, status, snoozedUntil) {
  store
    .dispatch('toggleStatus', {
      conversationId,
      status,
      snoozedUntil,
    })
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'));
    });
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return store.getters.getConversationById(item)?.status === status;
  });
}

function onContextMenuToggle(state) {
  isContextMenuOpen.value = state;
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

useEmitter('fetch_conversation_stats', () => {
  if (hasAppliedFiltersOrActiveFolders.value) return;
  store.dispatch('conversationStats/get', conversationFilters.value);
});

// NOTICE: KAPTION-INTEGRATION - Handle query params for date filtering (from reports chart click)
function applyQueryParamFilters() {
  const { created_at_from: createdFrom, created_at_to: createdTo } =
    route.query;

  if (!createdFrom || !createdTo) {
    return false;
  }

  // The backend uses strict inequality (> and <) with date casting.
  // To filter for a specific date range, we need to adjust:
  // - For is_greater_than: send the day BEFORE (so > Dec 31 = Jan 1+)
  // - For is_less_than: send the day AFTER (so < Jan 2 = up to Jan 1)
  const fromDate = parseISO(createdFrom);
  const toDate = parseISO(createdTo);
  const adjustedFrom = formatISO(subDays(fromDate, 1), {
    representation: 'date',
  });
  const adjustedTo = formatISO(addDays(toDate, 1), { representation: 'date' });

  // Build filter payload for created_at date range
  const dateFilters = [
    {
      attribute_key: 'created_at',
      filter_operator: 'is_greater_than',
      values: [adjustedFrom],
      query_operator: 'and',
      attribute_model: 'standard',
    },
    {
      attribute_key: 'created_at',
      filter_operator: 'is_less_than',
      values: [adjustedTo],
      attribute_model: 'standard',
    },
  ];

  // Apply the filters
  hasUrlFiltersApplied.value = true;
  hasUrlDateFilters.value = true;
  appliedFilter.value = dateFilters;
  // NOTE: We intentionally DO NOT call setConversationFilters here.
  // The server-side filter is sufficient, and client-side date filtering
  // can have timezone issues. By not setting appliedFilters in the store,
  // getFilteredConversations will return all conversations in allConversations
  // (which are already server-filtered).
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(dateFilters),
      page: 1,
    })
    .then(emitConversationLoaded);

  // Reset the flag after watchers have had a chance to fire
  // Using setTimeout to ensure all async watchers complete
  setTimeout(() => {
    hasUrlFiltersApplied.value = false;
  }, 100);

  return true;
}

// NOTICE: KAPTION-INTEGRATION - Sync Kaption selections with route (sidebar navigation)
// This function syncs selections AND sets the appropriate assignee filter
function syncKaptionSelectionsWithRoute() {
  // Skip if Kaption header is not enabled
  if (!useKaptionHeader.value) return;

  const routeName = route.name;
  const params = route.params;

  // NOTICE: KAPTION-INTEGRATION - Don't reset selections or assignee tab when
  // navigating to a conversation detail view (opening a conversation).
  // The sidebar should preserve its current filter state.
  if (isConversationDetailRoute(routeName)) {
    return;
  }

  // Clear all first
  clearKaptionSelections();

  // Track if we found a match (to set assignee filter appropriately)
  let foundMatch = false;

  // Set the appropriate selection based on route
  // Route names from conversation.routes.js:
  // - agent_conversations: /agent/:agentId
  // - agent_team_conversations: /agent/:agentId/team/:teamId
  // - team_conversations: /team/:teamId
  // - inbox_dashboard: /inbox/:inbox_id
  // - label_conversations: /label/:label
  // - folder_conversations: /custom_view/:id
  // - conversation_kaption_assistant: /kaption-assistants/conversations/assistant/:assistantId
  // - conversation_mentions: /mentions/conversations
  // - conversation_unattended: /unattended/conversations
  // - home: /dashboard (all conversations)

  if (
    (routeName === 'agent_conversations' ||
      routeName === 'agent_team_conversations') &&
    params.agentId
  ) {
    foundMatch = agentList.value.some(
      candidate => candidate.id === Number(params.agentId)
    );
    if (params.teamId) {
      const foundTeam = teamsList.value.find(
        candidate => candidate.id === Number(params.teamId)
      );
      if (foundTeam) kaptionSelectedTeam.value = foundTeam;
    }
  } else if (routeName === 'team_conversations' && params.teamId) {
    const foundTeam = teamsList.value.find(
      tm => tm.id === Number(params.teamId)
    );
    if (foundTeam) {
      kaptionSelectedTeam.value = foundTeam;
      foundMatch = true;
    }
  } else if (routeName === 'inbox_dashboard' && params.inbox_id) {
    const foundChannel = inboxesList.value.find(
      ch => ch.id === Number(params.inbox_id)
    );
    if (foundChannel) {
      kaptionSelectedChannel.value = foundChannel;
      foundMatch = true;
    }
  } else if (routeName === 'label_conversations' && params.label) {
    const foundLabel = kaptionLabels.value.find(lb => lb.name === params.label);
    if (foundLabel) {
      kaptionSelectedLabel.value = foundLabel;
      foundMatch = true;
    }
  } else if (routeName === 'folder_conversations' && params.id) {
    const foundFolder = folders.value.find(fd => fd.id === Number(params.id));
    if (foundFolder) {
      kaptionSelectedFolder.value = foundFolder;
      foundMatch = true;
    }
  } else if (
    (routeName === 'conversation_kaption_assistant' ||
      routeName === 'conversation_kaption_assistants') &&
    params.assistantId
  ) {
    const foundAssistant = getAssistant.value.find(
      ast => ast.id === Number(params.assistantId)
    );
    if (foundAssistant) {
      kaptionSelectedAssistant.value = foundAssistant;
      foundMatch = true;
    }
  }
  // For mentions, unattended, and home routes - no specific selection needed,
  // the title is handled by pageTitle computed in the component

  // When filtering by a specific category (team, channel, label, etc.),
  // default to 'all' assignee filter so user sees all conversations in that category
  if (
    foundMatch &&
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME
  ) {
    activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.ALL;
  }
}

useEventListener(conversationDynamicScroller, 'scroll', handleScroll);

onMounted(() => {
  // NOTICE: KAPTION-INTEGRATION - Default to 'all' assignee tab when Kaption header is enabled
  // This provides a better UX showing all conversations by default instead of just "mine"
  // Set this BEFORE dispatching filters so the correct value is used
  // NOTICE: KAPTION-INTEGRATION - When defaultAllConversations flag is enabled,
  // always default to ALL regardless of Kaption header
  if (defaultAllConversations.value) {
    const assigneeParam = route.query.assignee;
    if (assigneeParam === 'me') {
      // Redirect from My Inbox to All Conversations
      activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.ALL;
      router.replace({
        ...route,
        query: { ...route.query, assignee: undefined },
      });
    } else if (assigneeParam === 'unassigned') {
      activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.UNASSIGNED;
    } else {
      activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.ALL;
    }
    if (useKaptionHeader.value) {
      syncKaptionSelectionsWithRoute();
    }
  } else if (useKaptionHeader.value) {
    // NOTICE: KAPTION-INTEGRATION - Check for assignee query param from sidebar navigation
    const assigneeParam = route.query.assignee;
    const persisted = getPersistedAssigneeTab();
    if (assigneeParam === 'me') {
      activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.ME;
    } else if (assigneeParam === 'unassigned') {
      activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.UNASSIGNED;
    } else if (assigneeParam === 'unread') {
      // NOTICE: KAPTION-INTEGRATION - Deep-link from sidebar/topbar Unread filter
      activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.UNREAD;
    } else if (assigneeParam) {
      // Explicit query param present — use ALL
      activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.ALL;
    } else if (persisted) {
      // No explicit query param — restore persisted filter from session
      activeAssigneeTab.value = persisted;
    } else {
      // No persisted value either — default to ALL
      activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.ALL;
    }
    // Also sync selections with route now that feature flag is available
    syncKaptionSelectionsWithRoute();
  }

  if (props.assigneeId || props.teamId) {
    activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.ALL;
  }

  store.dispatch('setChatListFilters', conversationFilters.value);
  setFiltersFromUISettings();

  // NOTICE: KAPTION-INTEGRATION - Sort priority: URL param > persisted session > UISettings
  const urlSortParam = route.query.sort;
  const persistedSort = getPersistedSortOrder();
  if (
    urlSortParam &&
    Object.values(wootConstants.SORT_BY_TYPE).includes(urlSortParam)
  ) {
    activeSortBy.value = urlSortParam;
  } else if (persistedSort) {
    // Restore persisted sort order from session (overrides UISettings)
    activeSortBy.value = persistedSort;
  }

  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);

  // NOTICE: KAPTION-INTEGRATION - Listen for theme changes for dark mode
  themeUnsubscribe = themeEventEmitter.onThemeChange(event => {
    isDarkModeInternal.value = event.theme === 'dark';
  });

  // NOTICE: KAPTION-INTEGRATION - Check for query param filters first (from reports chart click)
  // If filters are applied from URL params, skip normal data fetch
  if (!applyQueryParamFilters()) {
    resetAndFetchData();
  }

  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
  }

  // NOTICE: KAPTION-INTEGRATION - Now that the correct defaults are set, allow
  // watchers to persist future changes to sessionStorage
  isFilterInitialized.value = true;
  persistAssigneeTab(activeAssigneeTab.value);
  persistSortOrder(activeSortBy.value);
});

// NOTICE: KAPTION-INTEGRATION - Cleanup theme listener on unmount
onUnmounted(() => {
  if (themeUnsubscribe) {
    themeUnsubscribe();
    themeUnsubscribe = null;
  }
});

const deleteConversationDialogRef = ref(null);
const selectedConversationId = ref(null);

async function deleteConversation() {
  // feature-permissions.CONVERSATION.3 — block the destructive dispatch when the
  // delete_conversation feature is not permitted for this user/account.
  if (!canUseFeature('delete_conversation')) return;
  try {
    await store.dispatch('deleteConversation', selectedConversationId.value);
    redirectToConversationList();
    selectedConversationId.value = null;
    deleteConversationDialogRef.value.close();
    useAlert(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
  } catch (error) {
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}

const handleDelete = conversationId => {
  // feature-permissions.CONVERSATION.3 — don't even open the confirmation dialog
  // when the delete_conversation feature is not permitted for this user/account.
  if (!canUseFeature('delete_conversation')) return;
  selectedConversationId.value = conversationId;
  deleteConversationDialogRef.value.open();
};

provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('assignAgent', onAssignAgent);
// NOTICE: KAPTION-INTEGRATION
provide('assignAssistant', onAssignAssistant);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('updateConversationStatus', toggleConversationStatus);
provide('toggleContextMenu', onContextMenuToggle);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);

// NOTICE: KAPTION-INTEGRATION — team-filter-scope.CLIENT.3
// CLIENT.1 needs the group's members to match against, and they are not part of the
// conversation payload, so load them when the group is opened.
watch(
  () => props.teamId,
  teamId => {
    if (teamId) store.dispatch('teamMembers/get', { teamId });
  },
  { immediate: true }
);

watch(activeTeam, () => resetAndFetchData());

watch(
  computed(() => props.assigneeId),
  () => resetAndFetchData()
);

// NOTICE: KAPTION-INTEGRATION - Watch for groups filter changes
watch(effectiveGroupsOnlyFilter, () => resetAndFetchData());

watch(
  computed(() => props.conversationInbox),
  () => resetAndFetchData()
);
watch(
  computed(() => props.label),
  () => resetAndFetchData()
);
watch(
  computed(() => props.conversationType),
  newConversationType => {
    // NOTICE: KAPTION-INTEGRATION - Set default tab to 'all' for kaption assistants
    if (newConversationType === 'kaption_assistants') {
      activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.ALL;
    } else if (
      activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL &&
      newConversationType !== 'kaption_assistants' &&
      !useKaptionHeader.value && // Don't reset when Kaption header is enabled (it defaults to 'all')
      !defaultAllConversations.value // Don't reset when defaultAllConversations flag is enabled
    ) {
      // Reset to 'me' when leaving kaption_assistants view (only for standard Chatwoot)
      activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.ME;
    }
    resetAndFetchData();
  }
);

// NOTICE: KAPTION-INTEGRATION - Watch for assistant ID changes
watch(
  computed(() => props.assistantId),
  () => resetAndFetchData()
);

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  resetAndFetchData();
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
  }
});

// NOTICE: KAPTION-INTEGRATION - Persist assignee tab and sort order to sessionStorage
// Skip persistence until onMounted has determined the correct default.
// Without this guard, the watcher fires immediately during setup and writes the
// ref's initial fallback (ME) to sessionStorage BEFORE onMounted can override it
// to ALL — causing a race condition where the persisted ME value sticks.
watch(
  activeAssigneeTab,
  newVal => {
    if (isFilterInitialized.value) {
      persistAssigneeTab(newVal);
    }
  },
  { immediate: true }
);

watch(
  activeSortBy,
  newVal => {
    if (isFilterInitialized.value) {
      persistSortOrder(newVal);
    }
  },
  { immediate: true }
);

// Watch route changes - NOTICE: KAPTION-INTEGRATION
watch(() => route.fullPath, syncKaptionSelectionsWithRoute, {
  immediate: true,
});

// NOTICE: KAPTION-INTEGRATION - Watch for assignee query param changes from sidebar navigation
watch(
  () => route.query.assignee,
  newAssignee => {
    if (!useKaptionHeader.value) return;
    // Only handle this when on the home route (all conversations view)
    if (route.name !== 'home' && route.name !== 'inbox_conversation') return;

    // NOTICE: KAPTION-INTEGRATION - Don't reset assignee tab when navigating to
    // a conversation detail view without explicit assignee query param.
    // This preserves the filter when opening a conversation from a filtered list.
    if (!newAssignee && isConversationDetailRoute(route.name)) {
      return;
    }

    // NOTICE: KAPTION-INTEGRATION - When defaultAllConversations flag is enabled,
    // redirect ?assignee=me to All Conversations
    if (defaultAllConversations.value && newAssignee === 'me') {
      activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.ALL;
      router.replace({
        ...route,
        query: { ...route.query, assignee: undefined },
      });
    } else if (newAssignee === 'me') {
      activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.ME;
    } else if (newAssignee === 'unassigned') {
      activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.UNASSIGNED;
    } else if (newAssignee === 'unread') {
      // NOTICE: KAPTION-INTEGRATION - Sidebar/topbar can deep-link to ?assignee=unread
      activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.UNREAD;
    } else if (!newAssignee) {
      // No assignee param means "all" — but only when explicitly navigating
      // to a list view, not when opening a conversation
      activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.ALL;
    }
    // Refetch with new filter
    resetAndFetchData();
  }
);

// NOTICE: KAPTION-INTEGRATION - Watch for sort query param changes from URL
watch(
  () => route.query.sort,
  newSort => {
    if (!useKaptionHeader.value) return;

    const validSort =
      newSort && Object.values(wootConstants.SORT_BY_TYPE).includes(newSort)
        ? newSort
        : wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;

    if (validSort !== activeSortBy.value) {
      activeSortBy.value = validSort;
      store.dispatch('setChatSortFilter', validSort);
      resetAndFetchData();
    }
  }
);

// Also watch data lists - they load asynchronously, so we need to re-sync when they're available
watch(
  [agentList, teamsList, inboxesList, kaptionLabels, folders, getAssistant],
  () => {
    // Only re-sync if we're on a route that should have a selection
    const routeName = route.name;
    const shouldHaveSelection = [
      'agent_conversations',
      'agent_team_conversations',
      'team_conversations',
      'inbox_dashboard',
      'label_conversations',
      'folder_conversations',
      'conversation_kaption_assistant',
      'conversation_kaption_assistants',
    ].includes(routeName);

    if (shouldHaveSelection) {
      syncKaptionSelectionsWithRoute();
    }
  },
  { deep: false }
);
</script>

<template>
  <div
    class="wp-card wp-clip relative flex flex-col flex-shrink-0 bg-n-solid-1 conversations-list-wrap"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout ? 'basis-full' : 'w-[340px] 2xl:w-[412px]',
      showKanbanToggle && isKanbanViewActive ? 'overflow-hidden' : '',
    ]"
  >
    <slot />
    <!-- NOTICE: KAPTION-INTEGRATION - Conditional header based on feature flag -->
    <KaptionChatListHeader
      v-if="useKaptionHeader"
      :page-title="pageTitle"
      :has-applied-filters="hasAppliedFilters"
      :has-active-folders="hasActiveFolders"
      :has-route-filters="hasActiveRouteFilters"
      :active-status="activeStatus"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-stats="conversationStats"
      :is-list-loading="chatListLoading && !conversationList.length"
      :is-dark-mode="isDarkMode"
      :favorites="kaptionResolvedFavorites"
      :folders="folders"
      :labels="kaptionLabels"
      :channels="inboxesList"
      :teams="teamsList"
      :assistants="getAssistant"
      :current-assignee-filter="activeAssigneeTab"
      :current-status-filter="activeStatus"
      :selected-favorite="kaptionSelectedFavorite"
      :selected-folder="kaptionSelectedFolder"
      :selected-label="kaptionSelectedLabel"
      :selected-channel="kaptionSelectedChannel"
      :selected-team="kaptionSelectedTeam"
      :selected-assistant="kaptionSelectedAssistant"
      :is-all-conversations-view="isAllConversationsView"
      :conversation-type="conversationType"
      :current-sort="activeSortBy"
      :can-mark-all-read="canMarkAllRead"
      :show-conversation-search="isKaptionConversationSearchEnabled"
      :conversation-search-query="kaptionConversationSearchQuery"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal"
      @reset-filters="resetAndFetchData"
      @toggle-layout="emit('toggleLayout')"
      @mark-all-read="openMarkAllReadDialog"
      @filter-change="handleKaptionFilterChange"
      @status-filter-change="handleKaptionStatusChange"
      @sort-change="handleKaptionSortChange"
      @favorite-click="handleKaptionFavoriteClick"
      @folder-click="handleKaptionFolderClick"
      @label-click="handleKaptionLabelClick"
      @channel-click="handleKaptionChannelClick"
      @team-click="handleKaptionTeamClick"
      @assistant-click="handleKaptionAssistantClick"
      @toggle-favorite="handleKaptionToggleFavorite"
      @navigate-to-mentions="handleKaptionNavigateToMentions"
      @navigate-to-unattended="handleKaptionNavigateToUnattended"
      @navigate-to-all-conversations="handleKaptionNavigateToAllConversations"
      @conversation-search-query-change="
        handleKaptionConversationSearchQueryChange
      "
    />
    <ChatListHeader
      v-else
      :page-title="pageTitle"
      :has-applied-filters="hasAppliedFilters"
      :has-active-folders="hasActiveFolders"
      :active-status="activeStatus"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-stats="conversationStats"
      :is-list-loading="chatListLoading && !conversationList.length"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal"
      @reset-filters="resetAndFetchData"
      @basic-filter-change="onBasicFilterChange"
    />

    <!-- NOTICE: KAPTION-INTEGRATION - Full-path filters remain visibly active. -->
    <div
      v-if="hasActiveRouteFilters"
      data-testid="conversation-route-filters"
      class="flex flex-wrap items-center gap-2 px-3 py-2 border-y border-n-weak bg-n-alpha-black2"
    >
      <span class="text-xs font-medium text-n-slate-10">
        {{ $t('KAPTION_PLATFORM.CHAT_LIST.FILTERED_BY') }}
      </span>
      <span
        v-for="filter in activeRouteFilters"
        :key="filter.key"
        :data-testid="`conversation-filter-${filter.key}`"
        class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border rounded-lg border-n-weak bg-n-solid-1 text-n-slate-12"
      >
        <i :class="filter.icon" class="size-3.5 text-n-slate-10" />
        <span class="text-n-slate-10">{{ filter.label }}</span>
        <span>{{ filter.value }}</span>
      </span>
    </div>

    <!-- NOTICE: KAPTION-INTEGRATION - Conditional teleport target -->
    <TeleportWithDirection
      v-if="showAddFoldersModal"
      :to="
        useKaptionHeader
          ? '#kaptionSaveFilterTeleportTarget'
          : '#saveFilterTeleportTarget'
      "
    >
      <SaveCustomView
        v-model="appliedFilter"
        :custom-views-query="foldersQuery"
        :open-last-saved-item="openLastSavedItemInFolder"
        @close="onCloseAddFoldersModal"
      />
    </TeleportWithDirection>

    <DeleteCustomViews
      v-if="showDeleteFoldersModal"
      v-model:show="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />

    <!-- NOTICE: KAPTION-INTEGRATION - Hide ChatTypeTabs when Kaption header is active (cycling filter is built-in) -->
    <ChatTypeTabs
      v-if="
        !hasAppliedFiltersOrActiveFolders &&
        !useKaptionHeader &&
        !(showKanbanToggle && isKanbanViewActive)
      "
      :items="assigneeTabItems"
      :active-tab="activeAssigneeTab"
      is-compact
      @chat-tab-change="updateAssigneeTab"
    />

    <!-- NOTICE: KAPTION-INTEGRATION - Kanban view (shown when toggle is active) -->
    <KaptionSalesKanban
      v-if="showKanbanToggle && isKanbanViewActive"
      :is-dark-mode="isDarkMode"
    />

    <!-- NOTICE: KAPTION-INTEGRATION - Single-pane search replaces the normal list while active. -->
    <KaptionConversationSearchPane
      v-if="isKaptionConversationSearchActive"
      :query="kaptionConversationSearchQuery"
      :label="label"
      :team-id="teamId"
      :assignee-id="assigneeId"
      :folders-id="foldersId"
      :conversation-type="conversationType"
      :show-assignee="showAssigneeInConversationCard"
    />

    <!-- Standard list view (hidden when kanban is active) -->
    <p
      v-if="
        !chatListLoading &&
        !conversationList.length &&
        !(showKanbanToggle && isKanbanViewActive) &&
        !isKaptionConversationSearchActive
      "
      class="flex items-center justify-center p-4 overflow-auto"
    >
      {{ $t('CHAT_LIST.LIST.404') }}
    </p>
    <ConversationBulkActions
      v-if="
        selectedConversations.length &&
        !(showKanbanToggle && isKanbanViewActive) &&
        !isKaptionConversationSearchActive
      "
      :conversations="selectedConversations"
      :all-conversations-selected="allConversationsSelected"
      :selected-inboxes="uniqueInboxes"
      :show-open-action="allSelectedConversationsStatus('open')"
      :show-resolved-action="allSelectedConversationsStatus('resolved')"
      :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
      @select-all-conversations="toggleSelectAll"
      @assign-agent="onAssignAgent"
      @assign-assistant="onAssignAssistant"
      @update-conversations="onUpdateConversations"
      @assign-labels="onAssignLabels"
      @assign-team="onAssignTeamsForBulk"
    />
    <div
      v-show="
        !(showKanbanToggle && isKanbanViewActive) &&
        !isKaptionConversationSearchActive
      "
      ref="conversationListRef"
      class="flex-1 overflow-hidden conversations-list hover:overflow-y-auto"
      :class="{ 'overflow-hidden': isContextMenuOpen }"
    >
      <DynamicScroller
        ref="conversationDynamicScroller"
        :items="decoratedConversationList"
        :min-item-size="24"
        class="w-full h-full overflow-auto"
      >
        <template #default="{ item, index, active }">
          <!--
            If we encounter resizing issues, we can set the `watchData` prop to true
            this will deeply watch the entire object instead of just size dependencies
            But it can impact performance
          -->
          <DynamicScrollerItem
            :item="item"
            :active="active"
            :data-index="index"
            :size-dependencies="[
              item.messages,
              item.labels,
              item.uuid,
              item.inbox_id,
              item.pinned,
            ]"
          >
            <!-- NOTICE: KAPTION-INTEGRATION - Pinned section separator -->
            <div
              v-if="item.__separator"
              class="px-3 py-1 text-xxs font-semibold uppercase tracking-wider text-n-slate-10 bg-n-alpha-1/40 border-b border-n-slate-3 flex items-center gap-1"
              :class="{
                'text-n-amber-11': item.id === '__pinned_section_header__',
              }"
            >
              <span
                v-if="item.id === '__pinned_section_header__'"
                class="i-lucide-pin size-3"
              />
              {{ item.label }}
            </div>
            <ConversationItem
              v-else
              :source="item"
              :label="label"
              :team-id="teamId"
              :assignee-id="assigneeId"
              :folders-id="foldersId"
              :conversation-type="conversationType"
              :show-assignee="showAssigneeInConversationCard"
              @select-conversation="selectConversation"
              @de-select-conversation="deSelectConversation"
            />
          </DynamicScrollerItem>
        </template>
        <template #after>
          <div v-if="chatListLoading" class="flex justify-center my-4">
            <Spinner class="text-n-brand" />
          </div>
          <p
            v-else-if="showEndOfListMessage"
            class="p-4 text-center text-n-slate-11"
          >
            {{ $t('CHAT_LIST.EOF') }}
          </p>
          <IntersectionObserver
            v-else
            :options="intersectionObserverOptions"
            @observed="loadMoreConversations"
          />
        </template>
      </DynamicScroller>
    </div>
    <Dialog
      ref="deleteConversationDialogRef"
      type="alert"
      :title="
        $t('CONVERSATION.DELETE_CONVERSATION.TITLE', {
          conversationId: selectedConversationId,
        })
      "
      :description="$t('CONVERSATION.DELETE_CONVERSATION.DESCRIPTION')"
      :confirm-button-label="$t('CONVERSATION.DELETE_CONVERSATION.CONFIRM')"
      @confirm="deleteConversation"
      @close="selectedConversationId = null"
    />
    <!-- NOTICE: KAPTION-INTEGRATION - Confirmation dialog for bulk mark-all-read -->
    <!-- SID: mark-all-read.UI.1 -->
    <Dialog
      ref="markAllReadDialogRef"
      type="alert"
      :title="$t('CHAT_LIST.MARK_ALL_READ.DIALOG_TITLE')"
      :description="$t('CHAT_LIST.MARK_ALL_READ.DIALOG_DESCRIPTION')"
      :confirm-button-label="$t('CHAT_LIST.MARK_ALL_READ.CONFIRM')"
      :is-loading="isMarkingAllRead"
      :disable-confirm-button="isMarkingAllRead"
      @confirm="confirmMarkAllRead"
    />
    <TeleportWithDirection
      v-if="showAdvancedFilters"
      to="#conversationFilterTeleportTarget"
    >
      <ConversationFilter
        v-model="appliedFilter"
        :folder-name="activeFolderName"
        :is-folder-view="hasActiveFolders"
        :folder="activeFolder"
        @apply-filter="onApplyFilter"
        @update-folder="onUpdateSavedFilter"
        @close="closeAdvanceFiltersModal"
      />
    </TeleportWithDirection>
    <!-- NOTICE: KAPTION-INTEGRATION - Bottom pill container (kanban toggle + action pill) -->
    <div v-if="useKaptionHeader" class="kanban-pills-container">
      <KaptionSalesKanbanTogglePill
        v-if="showKanbanToggle"
        :is-dark-mode="isDarkMode"
        :is-kanban-active="isKanbanViewActive"
        @toggle="toggleKanbanView"
      />
      <KaptionActionPill
        :is-dark-mode="isDarkMode"
        :scheduled-messages-count="actionPillScheduledCount"
        :scheduled-messages="actionPillScheduledMessages"
        :is-fetching-scheduled="actionPillIsFetching"
        :is-cancelling-scheduled="actionPillIsCancelling"
        account-level
        force-show
        :search-query="actionPillSearchQuery"
        @cancel-scheduled-message="handleAccountScheduledCancel"
        @search-scheduled="actionPillSetSearchQuery"
        @refresh-scheduled="actionPillRefresh"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
// NOTICE: KAPTION-INTEGRATION - Container for kanban toggle pill + action pill
.kanban-pills-container {
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  z-index: 39;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }

  // Override KaptionActionPill's absolute positioning when inside this container
  :deep(.action-pill-container) {
    position: static;
    transform: none;
    bottom: auto;
    left: auto;
  }
}
</style>
